import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Avatar, Box, Button, Typography, makeStyles } from "@material-ui/core";
import { Skeleton } from "@material-ui/lab";
import InsertDriveFileOutlinedIcon from "@material-ui/icons/InsertDriveFileOutlined";
import dayjs from "dayjs";
import TruncatedText from "components/TruncatedText";
import type { MalwareFile } from "@dashboard-v3/api";
import { initials } from "pages/MxheroShares/Malware/utils";

const FILES_PREVIEW = 10;
const SKELETON_ROWS = 4;

interface Props {
  files?: MalwareFile[];
  isLoading: boolean;
  error?: unknown;
}

export default function MalwareFiles({ files, isLoading, error }: Props) {
  const { t } = useTranslation("userActivity");
  const classes = useStyles();
  const [showAll, setShowAll] = useState(false);

  if (error) {
    return (
      <Typography variant="body2" color="error" className={classes.message}>
        {t("details.files.error")}
      </Typography>
    );
  }

  if (isLoading || !files) {
    return (
      <div data-testid="malware-files-loading">
        {Array.from({ length: SKELETON_ROWS }, (_, index) => (
          <div key={index} className={classes.row}>
            <Skeleton variant="rect" width={32} height={32} />
            <div>
              <Skeleton variant="text" width="60%" />
              <Skeleton variant="text" width="40%" />
            </div>
            <Skeleton variant="text" width="70%" />
            <Skeleton variant="text" width={90} />
          </div>
        ))}
      </div>
    );
  }

  if (files.length === 0) {
    return (
      <Typography
        variant="body2"
        color="textSecondary"
        className={classes.message}
      >
        {t("details.files.empty")}
      </Typography>
    );
  }

  const visible = showAll ? files : files.slice(0, FILES_PREVIEW);
  const hasMore = files.length > visible.length;

  return (
    <div>
      {visible.map(file => (
        <div key={file.id} className={classes.row} data-testid="malware-file">
          <div className={classes.fileIcon}>
            <InsertDriveFileOutlinedIcon fontSize="small" />
          </div>
          <Box minWidth={0}>
            <TruncatedText text={file.name} variant="body2" />
            <TruncatedText
              text={file.path}
              variant="caption"
              color="textSecondary"
              display="block"
            />
          </Box>
          <Box display="flex" alignItems="center" gridGap={10} minWidth={0}>
            <Avatar className={classes.avatar}>{initials(file.user)}</Avatar>
            <TruncatedText text={file.user} variant="body2" />
          </Box>
          <Typography variant="caption" color="textSecondary" noWrap>
            {dayjs(file.updatedAt).format("YYYY-MM-DD HH:mm")}
          </Typography>
        </div>
      ))}
      {hasMore && (
        <Box padding="8px 0 0">
          <Button
            size="small"
            color="primary"
            data-testid="malware-files-show-all"
            onClick={() => setShowAll(true)}
          >
            {t("details.files.showAll", { count: files.length })}
          </Button>
        </Box>
      )}
    </div>
  );
}

const useStyles = makeStyles(theme => ({
  message: { padding: "14px 0" },
  row: {
    display: "grid",
    gridTemplateColumns: "32px minmax(0, 1.4fr) minmax(0, 1fr) auto",
    alignItems: "center",
    gap: 12,
    padding: "10px 0",
    borderBottom: `1px solid ${theme.palette.divider}`,
    "&:last-child": { borderBottom: 0 },
  },
  fileIcon: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 32,
    height: 32,
    borderRadius: 6,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.secondary,
  },
  avatar: {
    width: 28,
    height: 28,
    fontSize: "0.72rem",
    backgroundColor: "#e3f4fb",
    color: "#04688a",
  },
}));
